import * as Yup from "yup";
import {
  fieldInitialValues,
  irrigationSystem,
  irrigationSystemDefaults,
} from "../config/fieldIntialValues";

export const validationSchema = (t) =>
  Yup.object({
    label: Yup.string().required(t("field_form.required")),
    //Soil type
    soil_type: Yup.object()
      .shape({
        clay: Yup.number()
          .min(0)
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.clay
              }`,
          ),
        sand: Yup.number()
          .min(0)
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.sand
              }`,
          ),
        silt: Yup.number()
          .min(0)
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.silt
              }`,
          ),
        permanent_wilting_point: Yup.number()
          .min(3, t("field_form.min_value_error", { min: 3 }))
          .max(40, t("field_form.max_value_error", { max: 40 }))
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.permanent_wilting_point
              }`,
          ),
        climate_zone: Yup.string().required(t("field_form.required")),
        field_capacity: Yup.number()
          .min(10, t("field_form.min_value_error", { min: 10 }))
          .max(50, t("field_form.max_value_error", { max: 50 }))
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.field_capacity
              }`,
          ),
        bulk_density: Yup.number()
          .min(1, t("field_form.min_value_error", { min: 1 }))
          .max(1.7, t("field_form.max_value_error", { max: 1.7 }))
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.bulk_density
              }`,
          ),
        soil_porosity: Yup.number()
          .min(1, t("field_form.min_value_error", { min: 1 }))
          .max(65, t("field_form.max_value_error", { max: 65 }))
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.soil_porosity
              }`,
          ),
        water_content_saturation: Yup.number()
          .min(35, t("field_form.min_value_error", { min: 35 }))
          .max(65, t("field_form.max_value_error", { max: 65 }))
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.water_content_saturation
              }`,
          ),
        water_content_saturation_by_sensor: Yup.number().required(
          t("field_form.required") +
            ` - ${t("field_form.suggested")}: ${
              fieldInitialValues.soil_type.water_content_saturation_by_sensor
            }`,
        ),
        saturated_hydraulic_conductivity: Yup.number()
          .min(0.01, t("field_form.min_value_error", { min: 0.01 }))
          .max(100, t("field_form.max_value_error", { max: 100 }))
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.saturated_hydraulic_conductivity
              }`,
          ),
        organic_matter: Yup.number()
          .min(0, t("field_form.min_value_error", { min: 0 }))
          .max(6, t("field_form.max_value_error", { max: 6 }))
          .required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                fieldInitialValues.soil_type.organic_matter
              }`,
          ),
      })
      .test(
        "texture-sum-100",
        t("soilType.soil_type_sum_error"),
        function (value) {
          if (!value || (!value.clay && !value.sand && !value.silt)) {
            return true;
          }

          const clay = Number(value.clay) || 0;
          const sand = Number(value.sand) || 0;
          const silt = Number(value.silt) || 0;
          const hasValues = [clay, sand, silt].some((val) => val > 0);
          if (!hasValues) {
            return true;
          }
          return clay + sand + silt === 100;
        },
      ),
    polygons: Yup.array()
      .test("valid-polygon", t("field_form.polygon_required"), (polys) => {
        if (!Array.isArray(polys) || polys.length === 0) return false;
        return polys.some(
          (p) => Array.isArray(p?.position) && p.position.length >= 3,
        );
      })
      .required(t("field_form.polygon_required")),
    irrigations_system: Yup.object().shape({
      system: Yup.string().required(t("field_form.required")),
      // Campi per sistema sprinkler
      number: Yup.number().when("system", {
        is: irrigationSystem.sprinkler,
        then: () =>
          Yup.number().required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                irrigationSystemDefaults.sprinkler.number
              }`,
          ),
        otherwise: () => Yup.number().notRequired(),
      }),
      s_rate: Yup.number().when("system", {
        is: irrigationSystem.sprinkler,
        then: () =>
          Yup.number().required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                irrigationSystemDefaults.sprinkler.s_rate
              }`,
          ),
        otherwise: () => Yup.number().notRequired(),
      }),
      // Campi per sistema drip
      number_driping_pipes: Yup.number().when("system", {
        is: (value) =>
          value === irrigationSystem.drip ||
          value === irrigationSystem.subterranean,
        then: () =>
          Yup.number().required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                irrigationSystem.drip
                  ? irrigationSystemDefaults.drip.number_driping_pipes
                  : irrigationSystemDefaults.subterranean.number_driping_pipes
              }`,
          ),
        otherwise: () => Yup.number().notRequired(),
      }),
      space_driping_bores: Yup.number().when("system", {
        is: irrigationSystem.drip,
        then: () =>
          Yup.number().required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                irrigationSystemDefaults.drip.space_driping_bores
              }`,
          ),
        otherwise: () => Yup.number().notRequired(),
      }),
      d_rate: Yup.number().when("system", {
        is: (value) =>
          value === irrigationSystem.drip ||
          value === irrigationSystem.subterranean,
        then: () =>
          Yup.number()
            .min(1, t("field_form.min_value_error", { min: 1 }))
            .max(8, t("field_form.max_value_error", { max: 8 }))
            .required(
              t("field_form.required") +
                ` - ${t("field_form.suggested")}: ${
                  irrigationSystemDefaults.drip.d_rate
                }`,
            ),
        otherwise: () => Yup.number().notRequired(),
      }),
      wetted_length: Yup.number().when("system", {
        is: (value) =>
          value === irrigationSystem.drip ||
          value === irrigationSystem.subterranean,
        then: () =>
          Yup.number()
            .typeError(t("field_form.must_be_number"))
            .required(
              t("field_form.required") +
                ` - ${t("field_form.suggested")}: ${
                  irrigationSystemDefaults.drip.wetted_length
                }`,
            ),
        otherwise: () => Yup.number().notRequired(),
      }),
      // Campi per sistema subterranean
      depth_pipes: Yup.number().when("system", {
        is: irrigationSystem.subterranean,
        then: () =>
          Yup.number()
            .min(10, t("field_form.min_value_error", { min: 10 }))
            .max(50, t("field_form.max_value_error", { max: 50 }))
            .required(
              t("field_form.required") +
                ` - ${t("field_form.suggested")}: ${
                  irrigationSystemDefaults.subterranean.depth_pipes
                }`,
            ),
        otherwise: () => Yup.number().notRequired(),
      }),
      spacing_dripping_bore: Yup.number().when("system", {
        is: irrigationSystem.subterranean,
        then: () =>
          Yup.number()
            .min(20, t("field_form.min_value_error", { min: 20 }))
            .max(150, t("field_form.max_value_error", { max: 150 }))
            .required(
              t("field_form.required") +
                ` - ${t("field_form.suggested")}: ${
                  irrigationSystemDefaults.subterranean.spacing_dripping_bore
                }`,
            ),
        otherwise: () => Yup.number().notRequired(),
      }),
      // Campo per surface irrigation
      surface_irrigation_rate: Yup.number().when("system", {
        is: irrigationSystem.surface_irrigation,
        then: () =>
          Yup.number().required(
            t("field_form.required") +
              ` - ${t("field_form.suggested")}: ${
                irrigationSystemDefaults["surface irrigation"]
                  .surface_irrigation_rate
              }`,
          ),
        otherwise: () => Yup.number().notRequired(),
      }),
    }),
    crop: Yup.object({
      type: Yup.string().required(
        t("field_form.required") +
          ` - ${t("field_form.suggested")}: ${fieldInitialValues.crop.type}`,
      ),
      planting_date: Yup.string().when("type", {
        is: "annual",
        then: () => Yup.string().required(t("field_form.required")),
        otherwise: () => Yup.string().notRequired(),
      }),
      flowering_date: Yup.string().when("type", {
        is: "perennial",
        then: () => Yup.string().required(t("field_form.required")),
        otherwise: () => Yup.string().notRequired(),
      }),
      expected_harvest_date: Yup.string().required(t("field_form.required")),
      distance_line: Yup.number()
        .min(50, t("field_form.min_value_error", { min: 50 }))
        .max(800, t("field_form.max_value_error", { max: 800 }))
        .required(
          t("field_form.required") +
            ` - ${t("field_form.suggested")}: ${
              fieldInitialValues.crop.distance_line
            }`,
        ),
      distance_individuals: Yup.number()
        .min(20, t("field_form.min_value_error", { min: 20 }))
        .max(600, t("field_form.max_value_error", { max: 600 }))
        .required(
          t("field_form.required") +
            ` - ${t("field_form.suggested")}: ${
              fieldInitialValues.crop.distance_individuals
            }`,
        ),
      species: Yup.string().required(
        t("field_form.required") + ` - ${t("field_form.suggested")}: citrus`,
      ),
    }).test(
      "harvest-date-after-reference",
      t("field_form.harvest_date_must_be_after"),
      function (value) {
        if (!value || !value.expected_harvest_date) {
          return true;
        }

        const parseDate = (dateStr) => {
          const [day, month, year] = dateStr.split("/");
          return new Date(year, month - 1, day);
        };

        const harvestDate = parseDate(value.expected_harvest_date);

        if (value.type === "annual" && value.planting_date) {
          const plantingDate = parseDate(value.planting_date);
          if (harvestDate <= plantingDate) {
            return this.createError({
              path: "crop.expected_harvest_date",
              message: t("field_form.harvest_date_must_be_after"),
            });
          }
        }

        if (value.type === "perennial" && value.flowering_date) {
          const floweringDate = parseDate(value.flowering_date);
          if (harvestDate <= floweringDate) {
            return this.createError({
              path: "crop.expected_harvest_date",
              message: t("field_form.harvest_date_must_be_after"),
            });
          }
        }

        return true;
      },
    ),
  });
